import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const CommissionTracker = ({ agent, onRequestPayout }) => {
  const [selectedPeriod, setSelectedPeriod] = useState('current');

  const periods = [
    { value: 'current', label: 'Current Month' },
    { value: 'last', label: 'Last Month' },
    { value: 'quarter', label: 'This Quarter' },
    { value: 'year', label: 'This Year' }
  ];

  // Mock commission breakdown data
  const commissionBreakdown = [
    {
      date: '2024-11-15',
      transactionId: 'TXN-789012',
      customer: 'Sarah Johnson',
      amount: 1250.00,
      commission: 43.75,
      rate: 3.5,
      status: 'paid'
    },
    {
      date: '2024-11-14',
      transactionId: 'TXN-789011',
      customer: 'David Chen',
      amount: 875.50,
      commission: 30.64,
      rate: 3.5,
      status: 'paid'
    },
    {
      date: '2024-11-14',
      transactionId: 'TXN-789010',
      customer: 'Maria Garcia',
      amount: 2100.00,
      commission: 84.00,
      rate: 4.0,
      status: 'pending'
    },
    {
      date: '2024-11-13',
      transactionId: 'TXN-789009',
      customer: 'James Wilson',
      amount: 650.75,
      commission: 22.78,
      rate: 3.5,
      status: 'paid'
    },
    {
      date: '2024-11-12',
      transactionId: 'TXN-789008',
      customer: 'Anna Kowalski',
      amount: 1800.00,
      commission: 72.00,
      rate: 4.0,
      status: 'paid'
    }
  ];

  const getStatusBadge = (status) => {
    const badges = {
      paid: { text: 'Paid', className: 'bg-success/10 text-success' },
      pending: { text: 'Pending', className: 'bg-warning/10 text-warning' },
      processing: { text: 'Processing', className: 'bg-primary/10 text-primary' }
    };
    return badges?.[status] || badges?.pending;
  };

  const totalPending = commissionBreakdown
    ?.filter(item => item?.status === 'pending')
    ?.reduce((sum, item) => sum + item?.commission, 0);

  const totalPaid = commissionBreakdown
    ?.filter(item => item?.status === 'paid')
    ?.reduce((sum, item) => sum + item?.commission, 0);

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">Commission Tracker</h3>
          <div className="flex items-center space-x-2">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e?.target?.value)}
              className="text-sm border border-border rounded px-3 py-1 bg-background"
            >
              {periods?.map(period => (
                <option key={period?.value} value={period?.value}>
                  {period?.label}
                </option>
              ))}
            </select>
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              iconPosition="left"
              onClick={() => console.log('Export commission report')}
            >
              Export
            </Button>
          </div>
        </div>

        {/* Commission Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-success/5 border border-success/20 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="CheckCircle" size={16} className="text-success" />
              <span className="text-sm font-medium text-success">Earned This Month</span>
            </div>
            <p className="text-2xl font-bold text-success">
              ${agent?.commissions?.thisMonth?.toLocaleString()}
            </p>
            <p className="text-xs text-success/70 mt-1">
              +${(agent?.commissions?.thisMonth - agent?.commissions?.lastMonth)?.toFixed(2)} vs last month
            </p>
          </div>

          <div className="bg-warning/5 border border-warning/20 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Clock" size={16} className="text-warning" />
              <span className="text-sm font-medium text-warning">Pending Payout</span>
            </div>
            <p className="text-2xl font-bold text-warning">
              ${agent?.commissions?.pending?.toLocaleString()}
            </p>
            <p className="text-xs text-warning/70 mt-1">
              Next payout: {agent?.commissions?.nextPayout}
            </p>
          </div>

          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="TrendingUp" size={16} className="text-primary" />
              <span className="text-sm font-medium text-primary">Total Earned</span>
            </div>
            <p className="text-2xl font-bold text-primary">
              ${agent?.commissions?.totalEarned?.toLocaleString()}
            </p>
            <p className="text-xs text-primary/70 mt-1">
              Lifetime earnings
            </p>
          </div>

          <div className="bg-muted/50 border border-border rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Percent" size={16} className="text-muted-foreground" />
              <span className="text-sm font-medium text-muted-foreground">Avg Commission Rate</span>
            </div>
            <p className="text-2xl font-bold text-foreground">
              {agent?.commissionRate}%
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Based on territory mix
            </p>
          </div>
        </div>

        {/* Commission Breakdown Table */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-foreground">Recent Commission Transactions</h4>
            <Button
              variant="ghost"
              size="sm"
              iconName="ExternalLink"
              iconPosition="right"
              onClick={() => console.log('View detailed history')}
            >
              View All
            </Button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-2 font-medium text-muted-foreground">Date</th>
                  <th className="text-left py-3 px-2 font-medium text-muted-foreground">Transaction</th>
                  <th className="text-left py-3 px-2 font-medium text-muted-foreground">Customer</th>
                  <th className="text-right py-3 px-2 font-medium text-muted-foreground">Amount</th>
                  <th className="text-right py-3 px-2 font-medium text-muted-foreground">Rate</th>
                  <th className="text-right py-3 px-2 font-medium text-muted-foreground">Commission</th>
                  <th className="text-center py-3 px-2 font-medium text-muted-foreground">Status</th>
                </tr>
              </thead>
              <tbody>
                {commissionBreakdown?.map((item, index) => {
                  const statusBadge = getStatusBadge(item?.status);
                  
                  return (
                    <tr key={index} className="border-b border-border/50 hover:bg-muted/20">
                      <td className="py-3 px-2 text-foreground">
                        {new Date(item?.date)?.toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </td>
                      <td className="py-3 px-2">
                        <span className="font-mono text-xs text-muted-foreground">
                          {item?.transactionId}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-foreground">{item?.customer}</td>
                      <td className="py-3 px-2 text-right font-medium text-foreground">
                        ${item?.amount?.toLocaleString()}
                      </td>
                      <td className="py-3 px-2 text-right text-muted-foreground">
                        {item?.rate}%
                      </td>
                      <td className="py-3 px-2 text-right font-medium text-foreground">
                        ${item?.commission?.toFixed(2)}
                      </td>
                      <td className="py-3 px-2 text-center">
                        <span className={`px-2 py-1 text-xs rounded-full ${statusBadge?.className}`}>
                          {statusBadge?.text}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Payout Information */}
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={20} className="text-primary mt-0.5" />
            <div className="flex-1">
              <h4 className="font-medium text-foreground mb-2">Commission Payout Schedule</h4>
              <div className="text-sm text-muted-foreground space-y-1">
                <p>• Commissions are calculated weekly and paid every Friday</p>
                <p>• Minimum payout threshold: $100</p>
                <p>• Payments are processed via ACH to your registered bank account</p>
                <p>• Transaction fees are deducted from gross commission amounts</p>
              </div>
              <div className="mt-3 flex items-center space-x-4">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="CreditCard"
                  iconPosition="left"
                  onClick={onRequestPayout}
                  disabled={agent?.commissions?.pending < 100}
                >
                  Request Payout
                </Button>
                <span className="text-xs text-muted-foreground">
                  Next automatic payout: {agent?.commissions?.nextPayout}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommissionTracker;